/** Mouse-driven board interactions for WbsBoardView: bar move (single + bulk),
 *  resize, and marquee select. Lives apart from the layout JSX so the heavy
 *  closure machinery (edge auto-scroll, retarget, day quantization) is testable
 *  and the view file stays a layout orchestrator. State that the render reads
 *  back (`drag`, `marquee`, `editingId`, `editAnchorRef`, `rootRef`) is owned
 *  here and surfaced through the returned object. */
import React from "react";
import { isTrainCarId, TRAIN_LEAF_KEY } from "../PullPlanGrid/train/trainReconcile";
import { bulkLaneShift, laneAtY } from "./boardLayout";
import type { Bar, BarSpan, LeafRow } from "./boardLayout";
import type { ScopeMember } from "../../../models";

type DragState =
  | {
      kind: "move";
      ids: Set<number>;
      dxDays: number;
      primaryDuration: number;
      target: { leafKey: string; startIndex: number; lane: number } | null;
    }
  | { kind: "resize"; id: number; dDays: number }
  | {
      kind: "bulk";
      ids: Set<number>;
      box: { left: number; top: number; width: number; height: number };
      dxPx: number;
      dyPx: number;
    }
  | null;

type MarqueeRect =
  | { left: number; top: number; width: number; height: number }
  | null;

export interface UseBoardDragOpts {
  leaves: LeafRow[];
  spanByIdRef: React.MutableRefObject<Map<number, BarSpan>>;
  calendar: string[];
  dayPx: number;
  laneHeight: number;
  labelWidth: number;
  readOnly: boolean;
  selectedIds: Set<number>;
  editable: (m: ScopeMember) => boolean;
  onMoveBars?: (
    moves: Array<{ activityId: number; startIndex: number; leafKey?: string; lane?: number }>,
  ) => void;
  onBulkDrop?: (intent: {
    ids: number[];
    dxDays: number;
    leafKey: string | null;
    lane: number | null;
    /** Per-bar target lane for a same-row VERTICAL bulk move (activity_id → lane).
     *  Absent for a horizontal-only move or a cross-row drop. */
    laneById?: Array<[number, number]>;
  }) => void;
  onResizeBar?: (activityId: number, durationDays: number) => void;
  onSelectionChange?: (next: Set<number>) => void;
  /** Freeze the given bars at their current lane (activity_id → lane). A drag
   *  calls this for every bar in the touched row(s) so moving one never re-packs
   *  the rest — the board behaves like a canvas. */
  onPinLanes?: (pairs: Array<[number, number]>) => void;
}

export interface UseBoardDrag {
  rootRef: React.MutableRefObject<HTMLDivElement | null>;
  drag: DragState;
  marquee: MarqueeRect;
  editingId: number | null;
  setEditingId: React.Dispatch<React.SetStateAction<number | null>>;
  editAnchorRef: React.MutableRefObject<{ left: number; top: number }>;
  startBarDrag: (e: React.MouseEvent, bar: Bar) => void;
  startResize: (e: React.MouseEvent, bar: Bar) => void;
  startMarquee: (e: React.MouseEvent) => void;
  dragMoveOffset: (id: number) => number;
  dragResizeExtra: (id: number) => number;
}

export function useBoardDrag(opts: UseBoardDragOpts): UseBoardDrag {
  const {
    leaves,
    spanByIdRef,
    calendar,
    dayPx,
    laneHeight,
    labelWidth,
    readOnly,
    selectedIds,
    editable,
    onMoveBars,
    onBulkDrop,
    onResizeBar,
    onSelectionChange,
    onPinLanes,
  } = opts;

  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = React.useState<DragState>(null);
  const [marquee, setMarquee] = React.useState<MarqueeRect>(null);
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const editAnchorRef = React.useRef<{ left: number; top: number }>({ left: 0, top: 0 });

  const selectionRef = React.useRef(selectedIds);
  selectionRef.current = selectedIds;

  // ---- bar drag (bulk) -----------------------------------------------------
  /** Bulk drag: ONE outline around the whole selection follows the cursor
   *  (no ghosts, no make-way); the drop hands the host a move-vs-duplicate
   *  decision. */
  const startBulkDrag = (e: React.MouseEvent, ids: Set<number>, grabbed?: Bar) => {
    const root = rootRef.current;
    if (!root || !onBulkDrop) return;
    // Train elements never bulk-move across rows (the train is a global template,
    // not a WBS leaf). A train-only selection bulk-shifts its cars horizontally in
    // place; a mixed selection drags ONLY the real activities and leaves cars put.
    const idArr = Array.from(ids);
    const trainOnly = idArr.length > 0 && idArr.every(isTrainCarId);
    const moveIds = trainOnly
      ? ids
      : new Set(idArr.filter((id) => !isTrainCarId(id)));
    if (moveIds.size === 0) return;
    // Home lane + leaf of every rendered bar, so a vertical bulk drag can shift
    // the selection by the lanes the grabbed bar moved — the same free placement
    // a single-bar drag gets, applied as a delta to keep the shape.
    const homeLaneById = new Map<number, number>();
    const homeLeafById = new Map<number, string>();
    let grabbedHomeLeaf: string | null = null;
    for (const lf of leaves) {
      for (const b of lf.bars) {
        homeLaneById.set(b.member.activity_id, b.lane);
        homeLeafById.set(b.member.activity_id, lf.key);
        if (grabbed && b.member.activity_id === grabbed.member.activity_id) {
          grabbedHomeLeaf = lf.key;
        }
      }
    }
    const grabbedHomeLane = grabbed
      ? homeLaneById.get(grabbed.member.activity_id) ?? 0
      : 0;
    const rootRect = root.getBoundingClientRect();
    let left = Infinity;
    let top = Infinity;
    let right = -Infinity;
    let bottom = -Infinity;
    for (const bid of Array.from(moveIds)) {
      const el = root.querySelector(`[data-wbs-bar="${bid}"]`) as HTMLElement | null;
      if (!el) continue;
      const r = el.getBoundingClientRect();
      left = Math.min(left, r.left - rootRect.left);
      top = Math.min(top, r.top - rootRect.top);
      right = Math.max(right, r.right - rootRect.left);
      bottom = Math.max(bottom, r.bottom - rootRect.top);
    }
    if (!Number.isFinite(left)) return;
    const box = { left, top, width: right - left, height: bottom - top };
    const scroller = root.closest(".pull-plan-grid-scroll") as HTMLElement | null;
    const startScroll = scroller?.scrollLeft ?? 0;
    const startX = e.clientX;
    const startY = e.clientY;
    let lastX = e.clientX;
    let lastY = e.clientY;
    let moved = false;
    let targetLeaf: string | null = null;
    let targetLane: number | null = null;
    const push = () => {
      const dxRaw = lastX - startX + ((scroller?.scrollLeft ?? 0) - startScroll);
      setDrag({
        kind: "bulk",
        ids,
        box,
        dxPx: Math.round(dxRaw / dayPx) * dayPx,
        dyPx: lastY - startY,
      });
    };
    push();
    const retarget = () => {
      const under = document.elementFromPoint(lastX, lastY) as HTMLElement | null;
      const rowEl = under?.closest?.("[data-wbs-leaf]") as HTMLElement | null;
      if (rowEl?.dataset.wbsLeaf) {
        targetLeaf = rowEl.dataset.wbsLeaf;
        const cellsEl = rowEl.querySelector(".wbs-board-cells") as HTMLElement | null;
        const top = (cellsEl ?? rowEl).getBoundingClientRect().top;
        targetLane = laneAtY(lastY, top, laneHeight);
      }
    };
    let rafId = 0;
    const EDGE = 48;
    const tick = () => {
      if (scroller) {
        const r = scroller.getBoundingClientRect();
        if (lastX > r.right - EDGE) scroller.scrollLeft += 16;
        else if (lastX < r.left + labelWidth + EDGE) scroller.scrollLeft -= 16;
      }
      push();
      rafId = window.requestAnimationFrame(tick);
    };
    rafId = window.requestAnimationFrame(tick);
    const onMove = (ev: MouseEvent) => {
      lastX = ev.clientX;
      lastY = ev.clientY;
      if (Math.abs(ev.clientX - startX) > 4 || Math.abs(ev.clientY - startY) > 4) {
        moved = true;
      }
      retarget();
      push();
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.cancelAnimationFrame(rafId);
      const dxDays = Math.round(
        (lastX - startX + ((scroller?.scrollLeft ?? 0) - startScroll)) / dayPx,
      );
      setDrag(null);
      if (!moved || (dxDays === 0 && targetLeaf == null)) return;
      if (trainOnly) {
        // Horizontal-only shift of the selected cars' static positions.
        if (!onMoveBars || dxDays === 0) return;
        const moves: Array<{ activityId: number; startIndex: number }> = [];
        for (const tid of Array.from(moveIds)) {
          const span = spanByIdRef.current.get(tid);
          if (span) {
            moves.push({ activityId: tid, startIndex: Math.max(0, span.startIndex + dxDays) });
          }
        }
        if (moves.length > 0) onMoveBars(moves);
        return;
      }
      // Real activities never land INSIDE the train lane — drop the row change so
      // a stray drop there just nudges them in time on their own row.
      const leafKey = targetLeaf === TRAIN_LEAF_KEY ? null : targetLeaf;
      // A same-row drop PINS each bar to its home lane + the lanes the grabbed bar
      // moved — so it stays exactly there and a left/right drag never re-packs it
      // onto a new lane. Only bars on the grabbed bar's own row are re-laned (a
      // multi-row selection leaves the other rows alone); a cross-row drop re-packs
      // lanes anyway, and the train lane never pins.
      const laneById =
        targetLane != null &&
        targetLeaf != null &&
        targetLeaf !== TRAIN_LEAF_KEY &&
        targetLeaf === grabbedHomeLeaf
          ? bulkLaneShift(
              Array.from(moveIds).filter((id) => homeLeafById.get(id) === grabbedHomeLeaf),
              homeLaneById,
              grabbedHomeLane,
              targetLane,
            )
          : undefined;
      // Freeze every bar in the touched row(s) at its current lane first, so the
      // move only shifts the dragged bars and never re-packs the rest.
      const touched = new Set<string>();
      moveIds.forEach((id) => {
        const lk = homeLeafById.get(id);
        if (lk) touched.add(lk);
      });
      if (leafKey) touched.add(leafKey);
      onPinLanes?.(
        Array.from(homeLaneById).filter(([id]) => touched.has(homeLeafById.get(id) ?? "")),
      );
      onBulkDrop({ ids: Array.from(moveIds), dxDays, leafKey, lane: targetLane, laneById });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // ---- bar drag (move) -----------------------------------------------------
  const startBarDrag = (e: React.MouseEvent, bar: Bar) => {
    if (e.button !== 0 || !editable(bar.member) || !onMoveBars) return;
    e.preventDefault();
    e.stopPropagation();
    const id = bar.member.activity_id;
    const barRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    if (e.shiftKey) {
      if (!onSelectionChange) return;
      const next = new Set(selectionRef.current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onSelectionChange(next);
      return;
    }
    const ids = selectionRef.current.has(id)
      ? new Set(selectionRef.current)
      : new Set([id]);
    if (ids.size > 1 && onBulkDrop) {
      startBulkDrag(e, ids, bar);
      return;
    }
    const primarySpan = spanByIdRef.current.get(id);
    const startX = e.clientX;
    const scroller = rootRef.current?.closest(".pull-plan-grid-scroll") as HTMLElement | null;
    const startScroll = scroller?.scrollLeft ?? 0;
    let lastX = e.clientX;
    let lastY = e.clientY;
    let moved = false;
    let dx = 0;
    let target: { leafKey: string; startIndex: number; lane: number } | null = null;
    const leafKeyById = new Map<number, string>();
    const laneById = new Map<number, number>();
    const lanesByLeafKey = new Map<string, number>();
    for (const lf of leaves) {
      lanesByLeafKey.set(lf.key, lf.lanes);
      for (const b of lf.bars) {
        leafKeyById.set(b.member.activity_id, lf.key);
        laneById.set(b.member.activity_id, b.lane);
      }
    }
    const homeLeaf = leafKeyById.get(id) ?? null;
    const homeLane = laneById.get(id) ?? 0;
    const startY = e.clientY;
    const calcDx = () =>
      Math.round((lastX - startX + ((scroller?.scrollLeft ?? 0) - startScroll)) / dayPx);
    const push = () => {
      setDrag({
        kind: "move",
        ids,
        dxDays: dx,
        primaryDuration: primarySpan?.durationDays ?? 1,
        target,
      });
    };
    const retarget = () => {
      if (!primarySpan) return;
      const under = document.elementFromPoint(lastX, lastY) as HTMLElement | null;
      const zoneEl = under?.closest?.("[data-wbs-leaf]") as HTMLElement | null;
      const leafKey = zoneEl?.dataset.wbsLeaf ?? target?.leafKey ?? homeLeaf;
      const startIndex = Math.max(
        0,
        Math.min(primarySpan.startIndex + dx, calendar.length - primarySpan.durationDays),
      );
      // Lane follows the cursor directly (no hysteresis: vertical movement is
      // seamless), measured against the lanes area via laneAtY.
      let lane = target?.lane ?? homeLane;
      const cellsEl = zoneEl?.querySelector(".wbs-board-cells") as HTMLElement | null;
      if (leafKey && cellsEl) {
        const maxLane = Math.max(0, (lanesByLeafKey.get(leafKey) ?? 1) - 1);
        lane = Math.min(maxLane, laneAtY(lastY, cellsEl.getBoundingClientRect().top, laneHeight));
      }
      if (leafKey != null) target = { leafKey, startIndex, lane };
    };
    const applyDx = () => {
      const next = calcDx();
      if (next !== dx) {
        dx = next;
        if (dx !== 0) moved = true;
      }
      retarget();
      push();
    };
    // Edge auto-scroll while dragging near the viewport edges, every frame.
    let rafId = 0;
    const EDGE = 48;
    const tick = () => {
      if (scroller) {
        const r = scroller.getBoundingClientRect();
        if (lastX > r.right - EDGE) scroller.scrollLeft += 16;
        else if (lastX < r.left + labelWidth + EDGE) scroller.scrollLeft -= 16;
      }
      applyDx();
      rafId = window.requestAnimationFrame(tick);
    };
    rafId = window.requestAnimationFrame(tick);
    const onMove = (ev: MouseEvent) => {
      lastX = ev.clientX;
      lastY = ev.clientY;
      // Vertical drags (lane/row changes) are moves too — not clicks.
      if (Math.abs(ev.clientY - startY) > 4) moved = true;
      applyDx();
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.cancelAnimationFrame(rafId);
      setDrag(null);
      if (!onMoveBars || !primarySpan) return;
      const finalTarget = target;
      const laneChanged = finalTarget != null && finalTarget.lane !== homeLane;
      if (
        moved &&
        (dx !== 0 || laneChanged || (finalTarget && finalTarget.leafKey !== homeLeaf))
      ) {
        const moves: Array<{ activityId: number; startIndex: number; leafKey?: string; lane?: number }> = [];
        const landed = finalTarget?.startIndex ??
          Math.max(0, Math.min(primarySpan.startIndex + dx, calendar.length - primarySpan.durationDays));
        const rowChange =
          finalTarget && finalTarget.leafKey !== homeLeaf ? finalTarget.leafKey : undefined;
        // Drop the dragged bar where it landed — it keeps the exact lane you
        // dropped it on (a pinned bar is always honored, overlap allowed).
        moves.push({
          activityId: id,
          startIndex: landed,
          leafKey: rowChange,
          lane: finalTarget?.lane,
        });
        ids.forEach((aid) => {
          if (aid === id) return;
          const span = spanByIdRef.current.get(aid);
          if (!span) return;
          moves.push({
            activityId: aid,
            startIndex: Math.max(
              0,
              Math.min(span.startIndex + dx, calendar.length - span.durationDays),
            ),
            leafKey: rowChange,
          });
        });
        // Freeze every bar in the touched row(s) at its current lane first, so the
        // move only shifts this bar and never re-packs the rest of the canvas.
        const touched = new Set<string>();
        if (homeLeaf) touched.add(homeLeaf);
        if (rowChange) touched.add(rowChange);
        onPinLanes?.(
          Array.from(laneById).filter(([aid]) => touched.has(leafKeyById.get(aid) ?? "")),
        );
        onMoveBars(moves);
      } else if (!moved) {
        // Plain click: open the edit popover (selection stays shift/marquee-only).
        onSelectionChange?.(new Set());
        editAnchorRef.current = { left: barRect.left, top: barRect.bottom + 6 };
        setEditingId((curr) => (curr === id ? null : id));
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // ---- resize --------------------------------------------------------------
  const startResize = (e: React.MouseEvent, bar: Bar) => {
    if (e.button !== 0 || !editable(bar.member) || !onResizeBar) return;
    e.preventDefault();
    e.stopPropagation();
    const id = bar.member.activity_id;
    const startX = e.clientX;
    let dDays = 0;
    const onMove = (ev: MouseEvent) => {
      dDays = Math.round((ev.clientX - startX) / dayPx);
      setDrag({ kind: "resize", id, dDays });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setDrag(null);
      if (dDays !== 0) {
        const span = spanByIdRef.current.get(id);
        if (span) {
          const next = Math.max(
            1,
            Math.min(span.durationDays + dDays, calendar.length - span.startIndex),
          );
          if (next !== span.durationDays) onResizeBar(id, next);
        }
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // ---- marquee selection ---------------------------------------------------
  const startMarquee = (e: React.MouseEvent) => {
    if (e.button !== 0 || readOnly || !onSelectionChange) return;
    const target = e.target as HTMLElement;
    if (
      target.closest("[data-wbs-bar]") ||
      target.closest(".wbs-board-popover") ||
      target.closest("button") ||
      target.closest("input")
    ) {
      return;
    }
    const root = rootRef.current;
    if (!root) return;
    e.preventDefault();
    setEditingId(null);
    const scroller = root.closest(".pull-plan-grid-scroll") as HTMLElement | null;
    // Anchor in CONTENT coordinates: clientXY minus the (live) root rect, so
    // the rectangle stays glued to the calendar while the board scrolls.
    const toContent = (cx: number, cy: number) => {
      const r = root.getBoundingClientRect();
      return { x: cx - r.left, y: cy - r.top };
    };
    const a = toContent(e.clientX, e.clientY);
    let lastCX = e.clientX;
    let lastCY = e.clientY;
    let swept = false;
    const update = () => {
      const p = toContent(lastCX, lastCY);
      const left = Math.min(a.x, p.x);
      const top = Math.min(a.y, p.y);
      const width = Math.abs(p.x - a.x);
      const height = Math.abs(p.y - a.y);
      setMarquee({ left, top, width, height });
      const r = root.getBoundingClientRect();
      const vl = r.left + left;
      const vt = r.top + top;
      const next = new Set<number>();
      root.querySelectorAll<HTMLElement>("[data-wbs-bar]").forEach((el) => {
        if (el.dataset.wbsEditable !== "1") return;
        const b = el.getBoundingClientRect();
        const overlap = b.left < vl + width && b.right > vl && b.top < vt + height && b.bottom > vt;
        if (overlap) next.add(Number(el.dataset.wbsBar));
      });
      onSelectionChange(next);
    };
    const onMove = (ev: MouseEvent) => {
      lastCX = ev.clientX;
      lastCY = ev.clientY;
      swept = true;
      update();
    };
    // Edge auto-scroll (both axes) — the content-anchored rect tracks the pan.
    let rafId = 0;
    const EDGE = 48;
    const tick = () => {
      if (scroller && swept) {
        const r = scroller.getBoundingClientRect();
        if (lastCX > r.right - EDGE) scroller.scrollLeft += 16;
        else if (lastCX < r.left + labelWidth + EDGE) scroller.scrollLeft -= 16;
        if (lastCY > r.bottom - EDGE) scroller.scrollTop += 14;
        else if (lastCY < r.top + EDGE) scroller.scrollTop -= 14;
        update();
      }
      rafId = window.requestAnimationFrame(tick);
    };
    rafId = window.requestAnimationFrame(tick);
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.cancelAnimationFrame(rafId);
      setMarquee(null);
      if (!swept) onSelectionChange(new Set());
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const dragMoveOffset = (id: number) =>
    drag?.kind === "move" && drag.ids.has(id) ? drag.dxDays * dayPx : 0;
  const dragResizeExtra = (id: number) =>
    drag?.kind === "resize" && drag.id === id ? drag.dDays * dayPx : 0;

  return {
    rootRef,
    drag,
    marquee,
    editingId,
    setEditingId,
    editAnchorRef,
    startBarDrag,
    startResize,
    startMarquee,
    dragMoveOffset,
    dragResizeExtra,
  };
}
